import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';

interface TooltipProps {
  content: ReactNode;
}

// [MCV-1520] Long enough to cross the 8px anchor gap between the icon and the
// panel, short enough that a panel can never be left sitting over the page.
// The previous iteration of this component left a portaled panel open
// indefinitely and it swallowed clicks elsewhere, so this stays bounded.
//
// 120ms proved too tight: under load the browser did not deliver the panel's
// mouseenter before the timer fired, which would flicker the tooltip away
// mid-transit on a busy machine just as it did in a loaded test run.
export const HOVER_CLOSE_DELAY_MS = 250;

/** Used as a sub-component of FormLabel
 *  Displays a tooltip if 'tooltip' prop is passed to the parent component i.e TextInput or Select */
export default function Tooltip(props: TooltipProps) {
  const { content } = props;

  // [MCV-1520] Hover visibility is tracked separately from Popover's own open
  // state, and the panel shows when *either* is set.
  //
  // The first attempt drove hover by programmatically clicking the button. That
  // made the control hover-only in practice: hovering opened the popover, and
  // the user's first real click immediately toggled it shut again. It also left
  // a portaled panel open that nothing subsequently closed, which intercepted
  // clicks elsewhere on the page.
  //
  // Keeping the two independent leaves Popover's native click and keyboard
  // handling untouched — one click opens, a second closes — while pointer and
  // focus merely preview the content.
  const [previewing, setPreviewing] = useState(false);

  // [MCV-1520] A focus or hover preview mounts the panel while Headless UI still
  // considers the Popover closed, so it never wires the trigger to the panel for
  // assistive tech (no `aria-controls`, no described-by). A stable id lets us
  // describe the trigger with the panel ourselves whenever the panel is shown.
  const tooltipId = useId();

  // The button and the portaled panel are separate elements with a gap between
  // them, so moving the pointer from one to the other fires `mouseleave` before
  // `mouseenter` — closing immediately would unmount the panel mid-transit and
  // put its content out of reach. A brief grace period spans that handover;
  // re-entering either element cancels it.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // [MCV-1520] Escape pressed while focus is inside the panel makes Headless UI
  // close the Popover and restore focus to the trigger. That restoring focus
  // would fire `onFocus` and re-preview, leaving the panel up after an explicit
  // dismissal. Arm this when we dismiss from an open Popover so the one focus
  // that restore fires is swallowed rather than treated as a fresh preview.
  const suppressRestoredFocus = useRef(false);

  const cancelPendingClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const showPreview = () => {
    cancelPendingClose();
    setPreviewing(true);
  };

  const hidePreviewAfterGrace = () => {
    cancelPendingClose();
    closeTimer.current = setTimeout(() => setPreviewing(false), HOVER_CLOSE_DELAY_MS);
  };

  // Blur is deliberate rather than incidental, so it closes at once.
  const hidePreviewNow = () => {
    cancelPendingClose();
    setPreviewing(false);
  };

  // Focus previews the content, except when the focus is Headless UI restoring
  // it to the trigger right after an Escape dismissal — that one is swallowed so
  // the dismissal sticks.
  const handleTriggerFocus = () => {
    if (suppressRestoredFocus.current) {
      suppressRestoredFocus.current = false;
      return;
    }
    showPreview();
  };

  useEffect(() => cancelPendingClose, []);

  return (
    <Popover className="relative m-0 p-0 flex items-center">
      {({ open }) => (
        <>
          <PopoverButton
            // These render inside <form>s, where a bare <button> defaults to
            // type="submit" — a stray submit is not what an info icon should do.
            type="button"
            aria-label="More information"
            // [MCV-1520] Focus/hover previews mount the panel without Headless
            // UI's own open state, so it never announces the panel as this
            // control's description. Point at it ourselves while it is shown.
            aria-describedby={open || previewing ? tooltipId : undefined}
            onMouseEnter={showPreview}
            onMouseLeave={hidePreviewAfterGrace}
            onFocus={handleTriggerFocus}
            onBlur={hidePreviewNow}
            // [MCV-1520] Dismissing has to clear the preview as well, or it
            // does nothing visible. Clicking focuses the button, which sets
            // `previewing`, so a second click flipped Popover's own `open` back
            // to false while `open || previewing` kept the panel on screen.
            // Escape had the same problem. `open` here is the pre-click value,
            // so this is correct whichever order the handlers run in.
            onClick={() => {
              if (open) hidePreviewNow();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && (open || previewing)) hidePreviewNow();
            }}
            // `focus:outline-none` on its own would leave keyboard users with no
            // indication of where they are, so a ring replaces the outline.
            className="ml-1 cursor-pointer focus:outline-none focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-primary-50 focus-visible:ring-offset-1"
          >
            <InformationCircleIcon className="size-4 text-primary-t-50 stroke-2" />
          </PopoverButton>
          {(open || previewing) && (
            <PopoverPanel
              static
              id={tooltipId}
              // Focus and hover previews open this while Headless UI's Popover
              // stays closed, so it is not otherwise announced. `tooltip` plus
              // the trigger's `aria-describedby` names it as the description.
              role="tooltip"
              anchor="top start"
              // [MCV-1520] Escape only reaches the trigger's handler when the
              // trigger holds focus. If a caller passes focusable content and
              // focus moves into the panel, the keypress lands here instead —
              // without this the preview would survive an explicit dismissal.
              // When the Popover is open, Headless UI will also close it and
              // restore focus to the trigger, so suppress the preview that
              // restored focus would otherwise reopen.
              onKeyDown={(event) => {
                if (event.key !== 'Escape') return;
                if (open) suppressRestoredFocus.current = true;
                hidePreviewNow();
              }}
              // Keeps the panel alive while the pointer is on it, so hovering
              // across from the icon works and any interactive content a caller
              // passes stays reachable.
              onMouseEnter={showPreview}
              onMouseLeave={hidePreviewAfterGrace}
              // [MCV-1520] `anchor` portals the panel to the document root,
              // where it carries no stacking context of its own. Inside a
              // FormDialog (z-[70] nested / z-[80] top) it rendered *behind*
              // the modal, so clicking the icon looked like it did nothing.
              className="z-[90] flex flex-col bg-white p-3 rounded-xl border border-grey-900 [--anchor-gap:8px] shadow-xl"
            >
              {content}
            </PopoverPanel>
          )}
        </>
      )}
    </Popover>
  );
}
